import React, { useEffect, useRef, useState } from "react";
import { BACKEND_URL } from "./../backendconfig";
import "./YouTubeManager.css";

const api = `${BACKEND_URL.replace(/\/+$/, "")}/youtube-manager-api.php`;

function videoId(value) {
  try {
    const url = new URL(value);

    if (!["http:", "https:"].includes(url.protocol)) return "";

    const host = url.hostname.toLowerCase();
    let id = "";

    if (host === "youtu.be") {
      id = url.pathname.replace(/^\/|\/$/g, "");
    } else if (
      ["youtube.com", "www.youtube.com", "m.youtube.com"].includes(host)
    ) {
      id =
        url.pathname === "/watch"
          ? url.searchParams.get("v") || ""
          : url.pathname.match(
              /^\/(?:shorts|embed|live)\/([^/]+)\/?$/
            )?.[1] || "";
    }

    return /^[A-Za-z0-9_-]{11}$/.test(id) ? id : "";
  } catch {
    return "";
  }
}

async function readResponse(response) {
  let data;

  try {
    data = await response.json();
  } catch {
    throw new Error(
      "The API did not return JSON. Check the PHP file and backend URL."
    );
  }

  if (!response.ok || !data.ok) {
    throw new Error(data.message || "Unable to process request.");
  }

  return data;
}

export default function YouTubeManager() {
  const [videos, setVideos] = useState([]);
  const [version, setVersion] = useState("");
  const [csrf, setCsrf] = useState("");

  const [selected, setSelected] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const editorRef = useRef(null);
  const titleRef = useRef(null);
  const requestBusy = useRef(false);

  useEffect(() => {
    const controller = new AbortController();

    async function load() {
      try {
        const response = await fetch(api, {
          credentials: "include",
          cache: "no-store",
          signal: controller.signal,
        });

        const data = await readResponse(response);

        if (!controller.signal.aborted) {
          setVideos(data.videos);
          setVersion(data.version);
          setCsrf(data.csrf);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setNotice({ type: "error", text: error.message });
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    load();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!editorOpen) return;

    titleRef.current?.focus();
    editorRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [editorOpen, selected]);

  function edit(index = null) {
    if (requestBusy.current) return;

    setSelected(index);
    setTitle(index === null ? "" : videos[index].title);
    setUrl(index === null ? "" : videos[index].url);
    setEditorOpen(true);
    setNotice(null);
  }

  function closeEditor() {
    setEditorOpen(false);
    setSelected(null);
    setTitle("");
    setUrl("");
  }

  async function send(action, index = null) {
    if (requestBusy.current) return;

    if (action === "save" && (!title.trim() || !videoId(url.trim()))) {
      setNotice({
        type: "error",
        text: "Enter a title and a valid YouTube video link.",
      });
      return;
    }

    if (
      action === "delete" &&
      !window.confirm("Delete this video?")
    ) {
      return;
    }

    requestBusy.current = true;
    setBusy(true);
    setNotice(null);

    try {
      const response = await fetch(api, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action,
          index,
          title: title.trim(),
          url: url.trim(),
          csrf,
          version,
        }),
      });

      const data = await readResponse(response);

      setVideos(data.videos);
setVersion(data.version);

if (action === "save" || action === "delete") {
  closeEditor();
}

setNotice({
  type: "success",
  text:
    action === "delete"
      ? "Video deleted successfully."
      : action === "move_up" || action === "move_down"
        ? "Video order updated successfully."
        : "Video saved successfully.",
});
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    } finally {
      requestBusy.current = false;
      setBusy(false);
    }
  }

  const query = search.trim().toLowerCase();

  // Keep the original array index after filtering for correct edit/delete.
  const filteredVideos = videos
    .map((video, index) => ({ ...video, index }))
    .filter((video) =>
      `${video.title} ${video.url}`.toLowerCase().includes(query)
    );

  const previewId = videoId(url.trim());

  return (
    <main className="ytm-page">
      <header className="ytm-header">
        <div>
          <span className="ytm-eyebrow">
            <i className="bi bi-youtube" aria-hidden="true" /> VIDEO LIBRARY
          </span>
          <h1>YouTube Manager</h1>
          <p>Manage your video titles and YouTube links.</p>
        </div>

        <span className="ytm-count">
          {videos.length} {videos.length === 1 ? "video" : "videos"}
        </span>
      </header>

      {notice && (
        <div
          className={`ytm-notice ytm-notice-${notice.type}`}
          role={notice.type === "error" ? "alert" : "status"}
        >
          {notice.text}
        </div>
      )}

      {loading ? (
        <div className="ytm-panel" role="status">
          Loading videos…
        </div>
      ) : !csrf ? (
        <div className="ytm-panel">
          Unable to load the manager. Check the API and reload.
        </div>
      ) : (
        <>
          {editorOpen && (
            <section className="ytm-panel ytm-editor" ref={editorRef}>
              <div className="ytm-section-heading">
                <h2>{selected === null ? "Add Video" : "Edit Video"}</h2>
                <button
                  type="button"
                  className="ytm-icon"
                  aria-label="Close editor"
                  disabled={busy}
                  onClick={closeEditor}
                >
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </button>
              </div>

              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  send("save", selected);
                }}
              >
                <fieldset disabled={busy} className="ytm-fieldset">
                  <div className="ytm-form-grid">
                    <label>
                      Video Title
                      <input
                        ref={titleRef}
                        required
                        value={title}
                        placeholder="Embroidery Sequin Design Punching"
                        onChange={(event) => setTitle(event.target.value)}
                      />
                    </label>

                    <label>
                      YouTube Link
                      <input
                        type="url"
                        required
                        value={url}
                        placeholder="https://www.youtube.com/watch?v=..."
                        onChange={(event) => setUrl(event.target.value)}
                      />
                    </label>
                  </div>

                  <p className="ytm-hint">
                    Supports YouTube watch, Shorts, live, embed, and youtu.be links.
                  </p>

                  {previewId && (
                    <div className="ytm-preview">
                      <img
                        src={`https://i.ytimg.com/vi/${previewId}/hqdefault.jpg`}
                        alt="Video thumbnail preview"
                      />
                      <div>
                        <strong>{title || "Video Preview"}</strong>
                        <a
                          href={`https://www.youtube.com/watch?v=${previewId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open on YouTube ↗
                        </a>
                      </div>
                    </div>
                  )}

                  <div className="ytm-actions">
                    <button type="submit" className="ytm-primary">
                      <i className="bi bi-check2" aria-hidden="true" />
                      {busy
                        ? "Saving…"
                        : selected === null
                          ? "Save Video"
                          : "Update Video"}
                    </button>

                    <button type="button" onClick={closeEditor}>
                      Cancel
                    </button>
                  </div>
                </fieldset>
              </form>
            </section>
          )}

          <section className="ytm-panel">
            <div className="ytm-toolbar">
              <h2>All Videos</h2>

              <div className="ytm-search">
                <i className="bi bi-search" aria-hidden="true" />
                <input
                  type="search"
                  aria-label="Search videos"
                  placeholder="Search title or link…"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>

              <button
                type="button"
                className="ytm-primary"
                disabled={busy}
                onClick={() => edit()}
              >
                <i className="bi bi-plus-lg" aria-hidden="true" />
                Add New
              </button>
            </div>

            <div className="ytm-table-scroll">
              <table className="ytm-table">
                <thead>
                  <tr>
                    <th scope="col">Video Title</th>
                    <th scope="col">YouTube Link</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredVideos.map((video) => {
                    const id = videoId(video.url);

                    return (
                      <tr key={video.index}>
                        <td>
                          <div className="ytm-video">
                            {id && (
                              <img
                                src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
                                alt=""
                                loading="lazy"
                              />
                            )}
                            <strong>{video.title}</strong>
                          </div>
                        </td>

                        <td>
                          {id ? (
                            <a
                              href={video.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {video.url}
                            </a>
                          ) : (
                            <span>{video.url}</span>
                          )}
                        </td>

                        <td>
                          <div className="ytm-row-actions">
<button
  type="button"
  className="ytm-icon"
  title="Move up"
  aria-label={`Move ${video.title} up`}
  disabled={
    busy || editorOpen || Boolean(query) || video.index === 0
  }
  onClick={() => send("move_up", video.index)}
>
  <i className="bi bi-arrow-up" aria-hidden="true" />
</button>

<button
  type="button"
  className="ytm-icon"
  title="Move down"
  aria-label={`Move ${video.title} down`}
  disabled={
    busy ||
    editorOpen ||
    Boolean(query) ||
    video.index === videos.length - 1
  }
  onClick={() => send("move_down", video.index)}
>
  <i className="bi bi-arrow-down" aria-hidden="true" />
</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {filteredVideos.length === 0 && (
                    <tr>
                      <td colSpan={3} className="ytm-empty">
                        {videos.length
                          ? "No matching videos."
                          : "No videos yet. Add your first video."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </main>
  );
}